import React, { useCallback } from 'react';
import { LogOut, User, Settings, Loader2 } from '@/components/icons/lucide';
import { useRouter } from 'expo-router';

import { useMenuVisible } from './use-menu-visible';

import { useIsLoadingUserData, useSignOut, useUser } from '@/states/auth';
import { useSignInModal } from '@/providers/modal/states';
import { Button } from '@/components/button';
import { Box, Text } from '@/components/ui';
import { cn } from '@/utils/cn';

type AvatarImageProps = {
  picture?: string | null;
  user: {
    name: string;
    email: string;
  };
};

const isApplePrivateRelayUser = (email: string): boolean => {
  return email.includes('privaterelay.appleid.com');
};

const AvatarImage: React.FC<AvatarImageProps> = React.memo(
  ({ picture, user }) => {
    if (isApplePrivateRelayUser(user.email) || !picture) {
      return (
        <div
          className="w-[30px] h-[30px] rounded-full"
          style={{
            background: 'linear-gradient(90deg, #FFC0CB, #FF69B4)',
          }}
        />
      );
    }

    return (
      <img
        src={picture}
        alt="Profile"
        className="w-[30px] h-[30px] rounded-full object-cover"
        referrerPolicy="no-referrer"
        crossOrigin="anonymous"
        onError={(e) => {
          console.error('Image loading error:', e);
          e.currentTarget.src = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(user.name)}`;
        }}
      />
    );
  }
);

export const SignInButton = React.memo(() => {
  const signInModal = useSignInModal();
  const user = useUser();
  const isLoadingUserData = useIsLoadingUserData();

  const { isMenuOpen, dismissMenu, openMenu } = useMenuVisible();
  const router = useRouter();

  const signOut = useSignOut();

  const handleSignOut = useCallback(() => {
    signOut();
    dismissMenu();
  }, [dismissMenu, signOut]);

  if (isLoadingUserData) {
    return (
      <Box
        className="w-8 h-8 rounded-full"
        bg="secondary"
        justify="center"
        align="center"
      >
        <Loader2 className="animate-spin-slow text-text-tertiary w-5 h-5" />
      </Box>
    );
  }

  if (user?.email) {
    const picture = user.profilePicture;

    return (
      <div
        className="relative"
        onMouseEnter={openMenu}
        onMouseLeave={dismissMenu}
      >
        <Box
          className="w-8 h-8 cursor-pointer border border-border/50 rounded-full"
          justify="center"
          align="center"
          hover="subtle"
        >
          <AvatarImage picture={picture} user={user} />
        </Box>

        {/* CSS-animated dropdown menu */}
        <div
          className={cn(
            'absolute right-0 mt-3 w-56 rounded-lg shadow-xl border border-white/10 focus:outline-none bg-background',
            'transition-all duration-200 ease-out',
            isMenuOpen
              ? 'opacity-100 translate-y-0 pointer-events-auto'
              : 'opacity-0 -translate-y-2 pointer-events-none'
          )}
          onMouseEnter={openMenu}
          onMouseLeave={dismissMenu}
        >
          <Box rounded="lg" className="py-1 overflow-hidden">
            <Box px={4} py={3} className="border-b border-border/20">
              {!isApplePrivateRelayUser(user.email) &&
                !user.name?.includes('undefined') && (
                  <Text variant="body-sm" weight="medium" color="primary">
                    {user.name}
                  </Text>
                )}
              <Box className="max-w-[250px] overflow-hidden">
                <Text
                  variant="body-sm"
                  color="secondary"
                  className="overflow-hidden text-ellipsis whitespace-nowrap"
                >
                  {user.email}
                </Text>
              </Box>
            </Box>

            <button
              onClick={() => router.navigate('/profile')}
              className="flex w-full items-center px-4 py-2 hover:bg-surface-secondary/50"
            >
              <User className="mr-2 h-4 w-4 text-text-secondary" />
              <Text variant="body-sm" color="secondary">
                Profile
              </Text>
            </button>

            <button
              onClick={() => router.navigate('/manage-subscription')}
              className="flex w-full items-center px-4 py-2 hover:bg-surface-secondary/50"
            >
              <Settings className="mr-2 h-4 w-4 text-text-secondary" />
              <Text variant="body-sm" color="secondary">
                {user.isSupporter ? 'Manage Support' : 'Support Demos'}
              </Text>
            </button>

            <button
              onClick={handleSignOut}
              className="flex w-full items-center px-4 py-2 hover:bg-surface-secondary/50"
            >
              <LogOut className="mr-2 h-4 w-4 text-danger" />
              <Text variant="body-sm" className="text-danger">
                Sign out
              </Text>
            </button>
          </Box>
        </div>
      </div>
    );
  }

  return (
    <Button
      onClick={() => signInModal.open()}
      variant="secondary"
      size="md"
      rounded="md"
      className="h-8"
    >
      Sign In
    </Button>
  );
});
